"use client";

import type { Episode, Show } from "@/types";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { registerGSAPPlugins, gsap } from "@/lib/animations/gsap-setup";

/* ─── Data ───
 *
 * Playlists + episodes are loaded from the project database (see
 * src/lib/shows.ts) and passed in as the `shows` prop from the page's
 * getServerSideProps. Poster + hero artwork live in object storage and
 * arrive as absolute CDN URLs on each show. To add or edit a playlist,
 * change the `shows` / `show_episodes` tables — nothing here needs to move.
 *
 * Episode thumbnails come straight from YouTube's CDN (i.ytimg.com/vi/{id})
 * so each thumbnail is ALWAYS the exact video's own frame — derived from
 * the stored YouTube id, never a stale placeholder.
 */
const ytThumb = (videoId: string) =>
  `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

// maxresdefault is not uploaded for every video (older YC 2014 lectures,
// for example). When the browser 404s on /maxresdefault.jpg, swap the
// <img> src to /hqdefault.jpg — which is always present.
function downgradeToHqOnError(e: React.SyntheticEvent<HTMLImageElement>) {
  const img = e.currentTarget;
  if (img.src.includes("/maxresdefault.")) {
    img.src = img.src.replace("/maxresdefault.", "/hqdefault.");
  }
}

// Duplicate the base set once so the fan wheel reads as a "full carousel"
// rather than a sparse arc, and re-index so each card's id matches its
// position in the array (the open/close animation looks cards up by id).
function buildCarousel(base: Show[]): Show[] {
  return [...base, ...base].map((s, i) => ({
    ...s,
    id: i,
    episodeList: s.episodeList.map((e) => ({ ...e })),
  }));
}

/* ─── URL state sync ───
 *
 * When a playlist opens, write ?playlist={slug} to the URL. When an episode
 * starts playing, add &episode={videoId}. Clears on close. This lets a user
 * copy the URL and share / report the exact playlist+episode state they're
 * looking at (e.g. if a specific video is broken, the URL identifies which).
 *
 * Uses replaceState — the browser back button doesn't navigate through
 * playlist open/close transitions, which avoids surprising UX on a
 * showcase page.
 */
function slugifyTitle(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function updateUrlParams(show: Show | null, videoId?: string | null) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (show) {
    url.searchParams.set("playlist", slugifyTitle(show.title));
    if (videoId) url.searchParams.set("episode", videoId);
    else url.searchParams.delete("episode");
  } else {
    url.searchParams.delete("playlist");
    url.searchParams.delete("episode");
  }
  window.history.replaceState({}, "", url.toString());
}

/* ─── Type icons ─── */
function TypeIcon({ type }: { type: Show["type"] }) {
  if (type === "series")
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
        <polyline points="17 2 12 7 7 2" />
      </svg>
    );
  if (type === "playlist")
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
    );
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><polygon points="10 8 16 12 10 16 10 8" />
    </svg>
  );
}

/* ─── Star icon ─── */
function StarIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

/* ─── Close icon ─── */
function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/* ─── Play icon ─── */
function PlayIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  );
}

/* ─── Carousel geometry ─── */
// angleState.current.angle is in CARD UNITS (float). 0 = card 0 centered,
// 1 = card 1 centered, etc.
const ANGLE_PER_CARD = 1;

// Fan layout constants. These are `let` rather than `const` because
// computeLayout() below retunes them for mobile viewports — on narrow
// screens a wide ±2 fan would shrink each card to ~75px wide, so we
// shift to a tighter 3-card arrangement with a gentler rotation per
// card and a higher pivot, keeping every card readable.
// More rotation per card and/or a pivot further below ⇒ more space between cards.
let ROTATION_PER_CARD = 16; // degrees between adjacent cards
// transform-origin Y percentage. >100% means the pivot is BELOW the card (so
// rotation fans the cards out to the sides and dips the extremes downward).
let PIVOT_Y_PERCENT = 250;
// How many cards the layout solver must keep fully on-screen (±LAYOUT_HALF_N).
// Anything farther out fades out and is allowed to clip — that's how we can
// have bigger cards AND more space between them without going off-screen.
let LAYOUT_HALF_N = 2;

// Card aspect ratio (cardW / cardH). Higher = wider cards, lower = more
// portrait. Appears in bboxHFactor / bboxWFactor / cardW below.
const CARD_ASPECT = 0.75;

// Mobile breakpoint — below this the layout switches to the tighter fan.
const MOBILE_VW_BREAKPOINT = 768;

// Visual bounds of the slider region on desktop.
// TOP_BOUND_RATIO sits below the hero copy. On 1500vw+ viewports the
// brush-script title reaches its clamp max (9rem) + pt-24 + eyebrow,
// landing around 44–47% of vh, so 0.50 keeps a comfortable margin
// before the card fan begins. Mobile uses a tighter 0.44 (see
// computeLayout) because pt shrinks and the title tops out at 4rem.
// BOT_BOUND_RATIO pushed to the viewport edge so side cards in the fan
// extend all the way down — reclaims the empty space beneath the slider.
const TOP_BOUND_RATIO = 0.50;
const BOT_BOUND_RATIO = 1.0;

/* ─── Interaction tuning ─── */
const AUTO_DELAY = 2000;
// DRAG_SENSITIVITY converts pixel-delta into card-units-delta on each
// pointermove frame. Retuned for mobile in computeLayout() below — short
// thumb swipes on narrow viewports need a higher multiplier, otherwise
// a 200px swipe barely moves a single card and the carousel feels dead.
let DRAG_SENSITIVITY = 0.003;
const MAX_VELOCITY = 0.15;
const CLICK_THRESHOLD = 8;
const FLING_PROJECTION_FRAMES = 10;

function computeLayout(vh: number, vw: number) {
  // Retune fan constants for mobile. With the desktop values (±2 cards,
  // 16° each, pivot at 250%) a 375px viewport squeezes cards down to
  // ~75px wide — unreadable. Mobile uses only ±1 must-fit cards with a
  // 10° fan and a higher pivot so the center card can grow to ~60% of
  // the viewport width.
  const isMobile = vw < MOBILE_VW_BREAKPOINT;
  ROTATION_PER_CARD = isMobile ? 10 : 16;
  LAYOUT_HALF_N = isMobile ? 1 : 2;
  PIVOT_Y_PERCENT = isMobile ? 200 : 250;
  // Boost swipe sensitivity on mobile — a thumb swipe is ~150-250px,
  // so 0.003 would move less than a full card. Bumping to 0.006 means
  // a ~170px swipe flips one card exactly, which matches the muscle
  // memory of native mobile carousels.
  DRAG_SENSITIVITY = isMobile ? 0.006 : 0.003;

  // Hero takes ~36% of vh on desktop (eyebrow + brush title) but closer
  // to 42% on mobile because the title's clamp floor is 4rem and the
  // eyebrow wraps one line. Give mobile a slightly lower TOP bound so
  // the card fan clears the title comfortably.
  const topBoundRatio = isMobile ? 0.44 : TOP_BOUND_RATIO;
  const topBoundary = vh * topBoundRatio;
  const botBoundary = vh * BOT_BOUND_RATIO;

  // Use LAYOUT_HALF_N (NOT the full ±(N-1)/2) as the "must fit" extreme. Cards
  // beyond LAYOUT_HALF_N are allowed to clip and are hidden by the opacity fade.
  const maxTheta = LAYOUT_HALF_N * ROTATION_PER_CARD * Math.PI / 180;
  const pivotOffset = (PIVOT_Y_PERCENT - 50) / 100; // pivot → card center, as factor of cardH
  const centerShiftY = pivotOffset * (1 - Math.cos(maxTheta));
  const centerShiftX = pivotOffset * Math.sin(maxTheta);
  // Bounding box of a rotated rect (cardW = CARD_ASPECT·cardH), as factor of cardH.
  const bboxHFactor = (CARD_ASPECT * Math.sin(maxTheta) + Math.cos(maxTheta)) / 2;
  const bboxWFactor = (CARD_ASPECT * Math.cos(maxTheta) + Math.sin(maxTheta)) / 2;

  // Vertical: center card top snug under tabs, ±LAYOUT_HALF_N card bottom above the bottom boundary.
  const topPadding = 12;
  const verticalSpace = botBoundary - topBoundary - topPadding;
  const maxCardHVert = verticalSpace / (0.5 + centerShiftY + bboxHFactor);

  // Horizontal: ±LAYOUT_HALF_N card's rotated bbox stays inside the viewport.
  // Mobile tightens the horizontal padding so the center card fills more of
  // the viewport.
  const horizontalPadding = isMobile ? 12 : 24;
  const horizontalSpace = vw / 2 - horizontalPadding;
  const maxCardHHoriz = horizontalSpace / (centerShiftX + bboxWFactor);

  const cardH = Math.floor(Math.min(maxCardHVert, maxCardHHoriz, 560));
  const cardW = Math.floor(cardH * CARD_ASPECT);

  const arcOriginY = topBoundary + cardH / 2 + topPadding;
  const centerYRatio = arcOriginY / vh;

  return { cardW, cardH, centerYRatio };
}

/* ─── Component ─── */
interface StreamShowcaseProps {
  shows: Show[];
  className?: string;
}

export default function StreamShowcase({ shows = [], className }: StreamShowcaseProps) {
  // Doubled, re-indexed carousel deck built from the playlists loaded out of
  // the database. Memoized so the id → ref mapping the animations rely on
  // stays stable across re-renders.
  const SHOWS = useMemo(() => buildCarousel(shows), [shows]);
  const N = SHOWS.length;

  const sectionRef = useRef<HTMLElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  // Inner wrapper inside each card — holds the bg/content and scales around its
  // own center (independent of the outer card's rotation-around-pivot-below).
  const cardInnerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const frameRef = useRef(0);
  const backdropRef = useRef<HTMLDivElement>(null);
  const expandedRef = useRef<HTMLDivElement>(null);

  // Angle-based scroll: angle=i means card at index i is centered. The
  // shelf boots centered on "Solo to SaaS" (base id 2) so the indie-
  // operator playlist is the first thing a visitor reads.
  const INITIAL_CENTER_ID = 2;
  const angleState = useRef({ angle: INITIAL_CENTER_ID, velocity: 0 });
  const autoTweenRef = useRef<gsap.core.Tween | null>(null); // GSAP tween for smooth auto-advance
  const dragState = useRef({
    active: false,
    startX: 0,
    lastX: 0,
    lastTime: 0,
    velocity: 0,
  });
  const lastInteraction = useRef(0);
  const pausedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const hoverRef = useRef(false);
  // After a modal close, freeze the slider at the user's card until they
  // actively interact again (drag / click a card / pick a filter). Without
  // this, the idle clock restarts at close-time and ~2s later auto-advance
  // starts rotating the slider on its own — the user perceives that drift
  // as the "jump when the card is put back on the slider".
  const suppressAutoAdvanceRef = useRef(false);
  const openTlRef = useRef<gsap.core.Timeline | null>(null);
  const openRectRef = useRef<DOMRect | null>(null); // card rect at open time, for close animation
  const openShowIdRef = useRef<number | null>(null);
  // Episode-click transition refs — used to morph a clicked episode thumbnail
  // up into the hero area (FLIP animation) before the video iframe appears.
  const heroContainerRef = useRef<HTMLDivElement>(null);
  const episodeCloneRef = useRef<HTMLDivElement>(null);
  const episodeTweenRef = useRef<gsap.core.Tween | null>(null);
  const layoutRef = useRef(
    computeLayout(
      typeof window !== "undefined" ? window.innerHeight : 800,
      typeof window !== "undefined" ? window.innerWidth : 1200,
    )
  );

  const [expandedShow, setExpandedShow] = useState<Show | null>(null);
  const [activeEpisode, setActiveEpisode] = useState<Episode | null>(null);
  const [playingVideo, setPlayingVideo] = useState(false);
  // Currently centered playlist index — used by the eyebrow chip to show a
  // "Now browsing · <title>" live indicator. Updated inside the physics tick
  // only when the rounded index changes, so re-renders stay rare.
  // Seed centerIdx to INITIAL_CENTER_ID so the eyebrow chip reads
  // "Now browsing · Solo to SaaS" on first paint instead of briefly
  // flashing "Ship With AI" before the first physics tick syncs.
  const [centerIdx, setCenterIdx] = useState(INITIAL_CENTER_ID);
  const lastCenterIdxRef = useRef(INITIAL_CENTER_ID);
  // Episode whose thumbnail is currently mid-morph into the hero area. While
  // non-null, a full-resolution clone of the episode thumb is rendered at
  // z-[1001] and animated from the thumb's click position up to the hero
  // container's rect. Nulled after the clone fades out once the iframe is in.
  const [transitionEpisode, setTransitionEpisode] = useState<Episode | null>(null);
  const [layout, setLayout] = useState(layoutRef.current);

  /* ── Diagnostic snapshot ──
   *
   * Captures the full slider + modal state at a named moment in the
   * interaction timeline so we can reconstruct what the browser was painting.
   * Invoked at click (0ms/100ms/1000ms), at close-start, at onReverseComplete
   * (pre-autoAlpha and post-transform-sync), and at 100ms/500ms/1000ms after
   * close completes. Search DevTools for "[SNAP " to see all entries ordered
   * by timestamp.
   */
  const snapshotSlider = useCallback((label: string) => {
    const a = angleState.current;
    const cards: Array<Record<string, unknown>> = [];
    for (let i = 0; i < N; i++) {
      const card = cardRefs.current[i];
      if (!card) continue;
      const inner = cardInnerRefs.current[i];
      const cs = getComputedStyle(card);
      let offset = i - a.angle;
      const halfN = N / 2;
      offset = ((offset % N) + N + halfN) % N - halfN;
      cards.push({
        i,
        title: SHOWS[i]?.title,
        expectedOffset: offset.toFixed(3),
        expectedRot: (offset * ROTATION_PER_CARD).toFixed(3),
        outerInline: card.style.transform,
        outerComputed: cs.transform,
        opacity: card.style.opacity,
        filter: card.style.filter,
        z: card.style.zIndex,
        innerInline: inner?.style.transform,
      });
    }
    const modal = expandedRef.current;
    let modalInfo: Record<string, unknown> | null = null;
    if (modal) {
      const cs = getComputedStyle(modal);
      modalInfo = {
        vis: cs.visibility,
        opacity: cs.opacity,
        transformInline: modal.style.transform,
        transformComputed: cs.transform,
        left: modal.style.left,
        top: modal.style.top,
        w: modal.style.width,
        h: modal.style.height,
      };
    }
    console.log(`[SNAP ${label}]`, {
      t: performance.now().toFixed(1),
      angle: a.angle.toFixed(4),
      velocity: a.velocity.toFixed(4),
      paused: pausedRef.current,
      suppressAutoAdv: suppressAutoAdvanceRef.current,
      hasAutoTween: !!autoTweenRef.current && autoTweenRef.current.isActive(),
      openTlProgress: openTlRef.current?.progress().toFixed(3),
      openTlReversed: openTlRef.current?.reversed(),
      modal: modalInfo,
      cards,
    });
  }, []);

  /* ── Open expanded view ──
   *
   * AUTHORITATIVE TIMELINE:
   * This function builds the single source-of-truth timeline for the open animation.
   * The close animation is literally `openTlRef.current.reverse()` — so any change
   * you make here (add a phase, change durations, add more tweens) is automatically
   * mirrored on close. Keep the rules below to preserve that property:
   *
   *   1. All visual state changes MUST go through tl.to() / tl.fromTo() / tl.set()
   *      on the timeline — never use gsap.set() side effects inside onComplete
   *      callbacks (those don't reverse).
   *   2. "Initial / closed" state goes through gsap.set() BEFORE the timeline.
   *      Those set calls define the state the reverse() will land on.
   *   3. React state changes (setExpandedShow(null), etc.) and final cleanup
   *      (clearProps, unpausing the physics tick) belong in onReverseComplete.
   */
  const openExpanded = useCallback((show: Show) => {
    if (suppressClickRef.current) return;
    pausedRef.current = true;
    angleState.current.velocity = 0;
    if (autoTweenRef.current) { autoTweenRef.current.kill(); autoTweenRef.current = null; }
    if (openTlRef.current) { openTlRef.current.kill(); openTlRef.current = null; }

    // Sync URL → ?playlist={slug}. Episode param is cleared until one plays.
    updateUrlParams(show);

    const card = cardRefs.current[show.id];
    if (!card) return;
    // getBoundingClientRect returns the AXIS-ALIGNED bbox of the rotated card,
    // which is wider/taller than the card itself. For the open animation we want
    // the clone to match the card's intrinsic dimensions, positioned at the
    // card's visual center.
    const bbox = card.getBoundingClientRect();
    const centerX = bbox.left + bbox.width / 2;
    const centerY = bbox.top + bbox.height / 2;
    const { cardW, cardH } = layoutRef.current;
    const rect = new DOMRect(centerX - cardW / 2, centerY - cardH / 2, cardW, cardH);
    openRectRef.current = rect;
    openShowIdRef.current = show.id;

    // Compute the clicked card's current visual state (rotation + inner scale)
    // so the modal can START visually identical to the card — critical for
    // non-centered clicks, because we no longer slide the slider to center
    // first. Without this match, the card (tilted, scaled) would swap with a
    // straight full-size modal and produce a pop.
    const currentAngle = angleState.current.angle;
    const halfN = N / 2;
    let cardOffset = show.id - currentAngle;
    cardOffset = ((cardOffset % N) + N + halfN) % N - halfN;
    const startRotation = cardOffset * ROTATION_PER_CARD;
    const startScale = 1 - Math.min(Math.abs(cardOffset), 1) * 0.15;
    console.log(
      `[StreamShowcase] Open from offset=${cardOffset.toFixed(2)} · rotation=${startRotation.toFixed(1)}° · scale=${startScale.toFixed(2)}`
    );

    console.log(`[StreamShowcase] Opening "${show.title}" — ${show.episodeList.length} episodes queued`);
    setExpandedShow(show);
    setActiveEpisode(show.episodeList[0]);
    setPlayingVideo(false);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const section = sectionRef.current;
        const expanded = expandedRef.current;
        const backdrop = backdropRef.current;
        if (!expanded || !backdrop || !section) return;

        const posterEl = expanded.querySelector<HTMLElement>("[data-poster]");
        const detailEl = expanded.querySelector<HTMLElement>("[data-detail]");
        if (!posterEl || !detailEl) return;

        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const finalW = Math.min(vw - 32, 1200);
        const finalH = vh * 0.92;
        const finalX = (vw - finalW) / 2;
        const finalY = (vh - finalH) / 2;

        const header = section.querySelector<HTMLElement>("[data-hero-title]")?.parentElement;
        const otherCards = cardRefs.current.filter((_, i) => i !== show.id).filter(Boolean) as HTMLElement[];
        const selectedCard = card;
        const detailOverlay = expanded.querySelector<HTMLElement>("[data-detail-overlay]");
        const episodeList = expanded.querySelector("[data-episode-list]");
        const episodeHeader = expanded.querySelector<HTMLElement>("[data-episode-header]");

        // ── CLOSED / initial state — what reverse() will land on ──
        // startRotation/startScale match the card's current visual so the
        // modal appears exactly ON the card, not as a separate upright shape.
        gsap.set(expanded, {
          left: rect.left, top: rect.top,
          width: rect.width, height: rect.height,
          opacity: 1,
          scaleX: startScale, scaleY: startScale,
          rotation: startRotation,
          rotationX: 0,
          yPercent: 0,
          // Smaller perspective = stronger 3D foreshortening during the flip.
          transformPerspective: 600,
          transformOrigin: "50% 50%",
        });

        // ── HANDOFF DIAGNOSTICS ──
        // Log the visual attributes of the card vs the modal at the moment of
        // handover. The modal visually replaces the card, so these should
        // match exactly — any difference (shadow, radius, bg, inner scale,
        // meta opacity, image opacity, grayscale filter) is what the user
        // perceives as a "jump" when the card fades to opacity 0 and the
        // modal takes its place. Same logic on close in reverse.
        const cardInnerForLog = cardInnerRefs.current[show.id];
        const cardImgForLog = card.querySelector<HTMLImageElement>("img");
        const cardMetaForLog = card.querySelector<HTMLElement>("[data-card-meta]");
        const cardGradForLog = card.querySelector<HTMLElement>("[data-card-gradient]");
        const cardOverlayForLog = card.querySelector<HTMLElement>("[data-card-overlay]");
        if (cardInnerForLog) {
          const cardCS = getComputedStyle(cardInnerForLog);
          const modalCS = getComputedStyle(expanded);
          console.log("[StreamShowcase] 🔍 handoff card → modal:", {
            rect: { left: rect.left.toFixed(1), top: rect.top.toFixed(1), w: rect.width, h: rect.height },
            cardOuterBBox: card.getBoundingClientRect(),
            cardInnerTransform: cardInnerForLog.style.transform,
            cardFilter: card.style.filter,
            cardOpacity: card.style.opacity,
            imgOpacity: cardImgForLog?.style.opacity,
            metaOpacity: cardMetaForLog?.style.opacity,
            gradientOpacity: cardGradForLog?.style.opacity,
            overlayOpacity: cardOverlayForLog?.style.opacity,
            shadowMatch: cardCS.boxShadow === modalCS.boxShadow,
            cardShadow: cardCS.boxShadow,
            modalShadow: modalCS.boxShadow,
            radiusMatch: cardCS.borderRadius === modalCS.borderRadius,
            cardRadius: cardCS.borderRadius,
            modalRadius: modalCS.borderRadius,
          });
        }
        gsap.set(posterEl, { opacity: 1 });
        gsap.set(detailEl, { opacity: 0 });
        gsap.set(backdrop, { opacity: 0 });
        if (detailOverlay) gsap.set(detailOverlay, { opacity: 0, y: 15 });
        if (episodeHeader) gsap.set(episodeHeader, { opacity: 0 });
        if (episodeList) {
          const items = episodeList.children;
          console.log(`[StreamShowcase] Preparing ${items.length} episodes for "${show.title}"`);
          gsap.set(items, { opacity: 0, xPercent: 40, willChange: "transform, opacity" });
        }

        // ── Re-sync GSAP's transform cache on every card ──
        // Between modals, the physics tick has been writing inline
        // `transform: rotate(Xdeg)` on each card directly — that bypasses
        // GSAP, so GSAP's internal `_gsap` cache still holds whatever
        // rotation was parsed during the PREVIOUS modal (at a different
        // angle). If we queue yPercent tweens on these cards now, GSAP
        // would combine yPercent with the STALE cached rotation, so Phase
        // A would start with the wrong rotation and visibly jump.
        // Routing the current physics-expected rotation through gsap.set
        // refreshes the _gsap cache to match the live inline style, so
        // the timeline's upcoming tweens start from an accurate baseline.
        const syncHalfN = N / 2;
        for (let i = 0; i < N; i++) {
          const c = cardRefs.current[i];
          if (!c) continue;
          let syncOffset = i - angleState.current.angle;
          syncOffset = ((syncOffset % N) + N + syncHalfN) % N - syncHalfN;
          gsap.set(c, {
            rotation: syncOffset * ROTATION_PER_CARD,
            yPercent: 0,
            x: 0, y: 0,
          });
        }

        // ── TIMELINE ──
        const tl = gsap.timeline({
          onReverseComplete: () => {
            // Close-side handoff diagnostics — mirror of the open log above.
            // At this instant the modal is back at the card's rect and the
            // card's opacity has just popped to 1 (via the reverse of
            // tl.set(selectedCard, {opacity:0}). If the card and modal
            // don't visually match, the pop here is the "jump on close".
            const cardInnerCS = cardInnerRefs.current[show.id]
              ? getComputedStyle(cardInnerRefs.current[show.id]!)
              : null;
            const modalCS = getComputedStyle(expanded);
            console.log("[StreamShowcase] 🔍 handoff modal → card:", {
              cardOpacity: card.style.opacity,
              modalOpacity: modalCS.opacity,
              shadowMatch: cardInnerCS?.boxShadow === modalCS.boxShadow,
              cardShadow: cardInnerCS?.boxShadow,
              modalShadow: modalCS.boxShadow,
              radiusMatch: cardInnerCS?.borderRadius === modalCS.borderRadius,
              filterMatch: getComputedStyle(card).filter === modalCS.filter,
              cardFilter: getComputedStyle(card).filter,
              modalFilter: modalCS.filter,
            });
            console.log("[anim] ◀ CLOSE complete · state cleared · physics tick resumed");
            // Snapshot BEFORE any cleanup — shows GSAP's final reverse state
            // as the browser would paint it if we did nothing else.
            snapshotSlider("close:onReverseComplete:pre-cleanup");
            // Hide the modal BEFORE React re-renders. React's style prop
            // drives `visibility` off `expandedShow`, so there's a window
            // between `setExpandedShow(null)` (async) and the actual DOM
            // commit where the modal is still `visibility: visible`. If we
            // do anything to its transforms in that window (e.g. clearProps)
            // the browser can paint the modal in an uninitialized position —
            // that's the flash the user was seeing. `autoAlpha: 0` writes
            // `visibility: hidden` + `opacity: 0` synchronously, so the
            // modal is guaranteed invisible through the render/paint cycle.
            gsap.set(expanded, { autoAlpha: 0 });
            setExpandedShow(null);
            setActiveEpisode(null);
            setPlayingVideo(false);
            // ── Sync wheel cards: match the physics-tick transform format ──
            // During the modal, GSAP animated `yPercent` on the other cards,
            // leaving them at something like `translate3d(0, 0%, 0) rotate(…)`.
            // The next physics tick writes a plain `rotate(Xdeg)` to inline
            // style, and the format change between those two strings can make
            // the browser re-rasterize the compositing layer for a frame
            // (visible as a flicker). Writing plain `rotate()` synchronously
            // here guarantees the FIRST paint after the reverse completes is
            // already in the final physics-format, so the next physics tick
            // writes the same string and causes zero re-rasterization.
            // Note: GSAP's `_gsap` cache goes stale here (we bypassed it),
            // but the NEXT modal open re-syncs it via `gsap.set` at the top
            // of `openExpanded`, so that staleness never gets read.
            const closePos = angleState.current.angle;
            const closeHalfN = N / 2;
            for (let i = 0; i < N; i++) {
              const c = cardRefs.current[i];
              if (!c) continue;
              let off = i - closePos;
              off = ((off % N) + N + closeHalfN) % N - closeHalfN;
              c.style.transform = `rotate(${off * ROTATION_PER_CARD}deg)`;
            }
            // Snapshot AFTER the transform sync but BEFORE unpausing physics.
            // This is the state the browser should paint on the next frame.
            snapshotSlider("close:onReverseComplete:post-sync");
            pausedRef.current = false;
            // Post-close snapshots at 100/500/1000ms — captures whether the
            // physics tick drifts, flickers, or auto-advances after resume.
            setTimeout(() => snapshotSlider("close:+100ms"), 100);
            setTimeout(() => snapshotSlider("close:+500ms"), 500);
            setTimeout(() => snapshotSlider("close:+1000ms"), 1000);
            // Freeze the auto-advance until the user actively interacts. We
            // do NOT set lastInteraction to performance.now() here — that
            // would start the 2s idle clock, and 2s later the slider would
            // quietly auto-rotate, which the user sees as a jump. Instead,
            // rely on suppressAutoAdvanceRef + pointerDown/filter click to
            // re-enable auto-advance on a deliberate interaction.
            suppressAutoAdvanceRef.current = true;
            lastInteraction.current = performance.now();
            openRectRef.current = null;
            openShowIdRef.current = null;
            openTlRef.current = null;
          },
        });
        openTlRef.current = tl;

        // 2× playback — the flip/scale phases were too slow. timeScale affects
        // both forward play AND reverse, so close speeds up identically.
        tl.timeScale(2);

        // Helper — stamp a console log at a timeline time. Fires both forward
        // AND reverse (via tl.call), so we can see the whole motion both ways.
        const mark = (time: number, msg: string) => {
          tl.call(() => console.log(`[anim] t=${time.toFixed(2)}s — ${msg}`), [], time);
        };

        mark(0, "▶ OPEN begins · backdrop in · other cards drop · Phase B scale 1→1.2 (1s) · Phase C lift yPercent 0→-50 (starts 0.2s)");

        // Hide the original card so the flipping clone isn't duplicated.
        // Using tl.set() so reverse() automatically restores opacity.
        tl.set(selectedCard, { opacity: 0 }, 0);

        // ── Phase A (0–1s): Other cards drag down 300% + fade; header fades; backdrop in ──
        // Duration 1s (timeline) = 0.5s real with timeScale 2 — deliberately
        // slower than before so the slider "hide" reads as a soft dismissal
        // rather than a snap. Reverse uses the same duration, so close is
        // smoother too.
        if (header) tl.to(header, { opacity: 0, y: -20, duration: 1.0, ease: "power2.in" }, 0);
        if (otherCards.length) {
          tl.to(otherCards, {
            yPercent: 300,
            opacity: 0,
            duration: 1.0,
            ease: "power2.in",
          }, 0);
        }
        tl.to(backdrop, { opacity: 1, duration: 1.0, ease: "power2.out" }, 0);

        // ── Phase B (0–1s): Selected card scales startScale → 1.2 and
        // un-tilts rotation → 0. For non-centered clicks the modal begins
        // tilted/shrunken to match the card; Phase B brings it upright and
        // to full size in one smooth motion. On reverse, rotation goes back
        // to the card's offset-rotation, so the close handoff matches too.
        tl.to(expanded, {
          scaleX: 1.2,
          scaleY: 1.2,
          rotation: 0,
          duration: 1.0,
          ease: "power2.out",
        }, 0);

        // ── Phase C (0.2–1s): Selected card moves up yPercent: -50 ──
        tl.to(expanded, {
          yPercent: -50,
          duration: 0.8,
          ease: "power2.out",
        }, 0.2);

        // ── Phase D (0.4–1.4s): Flip first half — squash to midpoint (1s) ──
        // Direction: rotationX: 0 → -90 (top tilts toward viewer, TOP bigger / BOTTOM smaller).
        const flipStart = 0.4;
        const flipHalfDur = 1.0;
        const flipMid = flipStart + flipHalfDur; // 1.4s
        mark(flipStart, "Phase D START · card-item flip rotationX 0 → -90 (1.0s) · TOP grows, BOTTOM shrinks (perspective 600) · scaleY stays 1.2 — 3D rotation alone collapses height to 0 at ±90");

        tl.to(expanded, {
          rotationX: -90,
          // No scaleY squash — rotationX alone collapses height to 0 at ±90°
          // via 3D perspective. Keeping scaleY constant means the CSS height
          // stays consistent throughout the flip, so the inner card (Phase E)
          // unfolds to the same height as the poster had.
          duration: flipHalfDur,
          ease: "power2.in",
        }, flipStart);

        // ── At flipMid: swap poster → detail AND flip rotation to +90 ──
        // The -90 → +90 snap is invisible because the card is edge-on at both
        // (scaleY: 0.012). Swapping to +90 means Phase E unfolds with BOTTOM
        // forward → BOTTOM bigger, TOP smaller — the opposite of Phase D.
        tl.set(posterEl, { opacity: 0 }, flipMid);
        tl.set(detailEl, { opacity: 1 }, flipMid);
        tl.set(expanded, { rotationX: 90 }, flipMid);
        mark(flipMid, "flipMid · poster → detail swap · rotation snaps -90 → +90 (edge-on, invisible)");

        // ── Phase E (1.4–1.9s): Inner card unfolds at POSTER SIZE + BOTTOM-FORWARD direction ──
        // - rotationX: +90 → 0 (bottom tilts back from forward to level — BOTTOM bigger, TOP smaller)
        // - scaleX/Y: untouched — both stay at 1.2 throughout, so the inner card
        //   has IDENTICAL width AND height to the poster at every matching moment.
        //   The flip's visual fold comes from rotationX + 3D perspective, not from scale.
        // - yPercent: untouched — stays at -50 (same lifted Y as Phase D end)
        const unfoldDur = flipHalfDur * 0.5; // 0.5s
        tl.to(expanded, {
          rotationX: 0,
          duration: unfoldDur,
          ease: "power3.out",
        }, flipMid);
        mark(flipMid, "Phase E START · inner-card unflip rotationX +90 → 0 (0.5s) · BOTTOM bigger, TOP smaller · scaleX AND scaleY both stay 1.2 — same width + height as poster");

        // ── Phase F: scale image wrapper to final panel ──
        // Starts 60% of the way through Phase E so the grow begins BEFORE the
        // flip completes — creates a continuous, butter-smooth transition from
        // "flipping in place" into "growing to final". Long duration + soft ease
        // make the size change feel effortless instead of snappy.
        const scaleUpStart = flipMid + unfoldDur * 0.6; // 1.7s
        const scaleUpDur = 1.4;                           // was 0.6 — real time (with timeScale 2) = 0.7s
        const innerEnd = scaleUpStart + scaleUpDur;       // 3.1s
        tl.to(expanded, {
          scaleX: 1,       // reset alongside width growing — net visual width grows smoothly
          scaleY: 1,       // reset alongside height growing — matches the new Phase E end state
          yPercent: 0,
          left: finalX,
          top: finalY,
          width: finalW,
          height: finalH,
          duration: scaleUpDur,
          ease: "power2.inOut", // gentle accel + soft decel — feels like the panel is breathing into place
        }, scaleUpStart);
        mark(scaleUpStart, `Phase F START · scale wrapper to final panel (${scaleUpDur}s, power2.inOut) · scaleX 1.2→1, scaleY 1.2→1, yPercent -50→0, size rect→final · OVERLAPS last 40% of Phase E`);
        mark(flipMid + unfoldDur, "Phase E END · flip fully unfolded (Phase F still running)");
        mark(innerEnd, "Phase F END · final panel settled · detail overlay fading in · episodes begin");

        // ── Phase F: Detail overlay text ──
        if (detailOverlay) {
          tl.to(detailOverlay, { opacity: 1, y: 0, duration: 0.45, ease: "power3.out" }, innerEnd - 0.4);
        }

        // ── Phase G: Episodes — header + staggered items ──
        const episodeDelay = innerEnd;
        if (episodeHeader) {
          tl.to(episodeHeader, { opacity: 1, duration: 0.35, ease: "power2.out" }, episodeDelay - 0.2);
        }
        if (episodeList) {
          const count = episodeList.children.length;
          console.log(`[anim] building timeline · ${count} episode items · stagger 80ms · duration 550ms · start @ t=${episodeDelay.toFixed(2)}s`);
          tl.to(episodeList.children, {
            opacity: 1,
            xPercent: 0,
            duration: 0.55,
            ease: "power3.out",
            stagger: 0.08,
          }, episodeDelay);
          mark(episodeDelay + 0.55 + 0.08 * (count - 1), `all ${count} episodes visible · open animation COMPLETE`);
        }
      });
    });
  }, []);

  /* ── Close expanded view ──
   *
   * Close is literally the reverse of the open timeline. Do not add bespoke
   * close animations here — any change you want in close must be expressed
   * in openExpanded() so the two always stay in sync.
   */
  const closeExpanded = useCallback(() => {
    const tl = openTlRef.current;
    if (!tl) return;
    // Clear URL params immediately — ?playlist= and ?episode= disappear
    // the moment the user hits close, even before the reverse animation
    // finishes.
    updateUrlParams(null);
    console.log(`[anim] ◀ CLOSE — reversing timeline from progress=${(tl.progress() * 100).toFixed(0)}% (timeScale ${tl.timeScale()}x)`);
    // Snapshot BEFORE the reverse tween starts — captures the slider state
    // while the modal is still fully open (other cards at yPercent: 300).
    snapshotSlider("close:before-reverse");
    setPlayingVideo(false);
    // Kill any in-flight episode morph so its clone doesn't keep flying
    // around after the modal has started closing.
    if (episodeTweenRef.current) {
      episodeTweenRef.current.kill();
      episodeTweenRef.current = null;
    }
    setTransitionEpisode(null);
    // If the reveal isn't fully complete yet, snap to a reasonable mid-state
    // by reversing from current progress. GSAP handles this natively.
    tl.reverse();
  }, [snapshotSlider]);

  /* ── Play episode ──
   *
   * FLIP morph: a subtle translucent overlay flies from the clicked
   * episode thumbnail up to the hero container, growing into the
   * placeholder's shape. Only AFTER the overlay lands do we mount the
   * iframe / switch its src — so the video never starts playing before
   * the visual transition has arrived (which previously felt disjointed
   * when clicking a second episode while the first was still playing,
   * because changing `activeEpisode` immediately retriggered the iframe).
   */
  const playEpisode = useCallback((episode: Episode, sourceBtn: HTMLElement) => {
    // NOTE: don't set `activeEpisode` here — that would change the iframe's
    // src immediately if a different episode is already playing. Defer both
    // activeEpisode and playingVideo to onComplete so the new video only
    // starts playing once the overlay has arrived at the hero.

    // Sync URL → ?playlist={slug}&episode={videoId}. Writing from closure's
    // expandedShow so we don't depend on the animation finishing.
    if (expandedShow) updateUrlParams(expandedShow, episode.videoId);

    const thumbImgEl = sourceBtn.querySelector<HTMLElement>("[data-ep-thumb]");
    const heroEl = heroContainerRef.current;

    // If refs aren't ready for whatever reason, fall back to the instant swap.
    if (!thumbImgEl || !heroEl) {
      setActiveEpisode(episode);
      setPlayingVideo(true);
      return;
    }

    const srcRect = thumbImgEl.getBoundingClientRect();
    const dstRect = heroEl.getBoundingClientRect();

    // Kill any in-flight episode-clone animation before starting a new one
    // (user can click a second episode mid-morph).
    if (episodeTweenRef.current) {
      episodeTweenRef.current.kill();
      episodeTweenRef.current = null;
    }
    setTransitionEpisode(episode);

    // Wait one frame for the overlay to mount with its ref attached, then FLIP.
    requestAnimationFrame(() => {
      const clone = episodeCloneRef.current;
      if (!clone) {
        setPlayingVideo(true);
        setTransitionEpisode(null);
        return;
      }

      // Animate the overlay's actual `width`/`height` (not transform-scale)
      // so `borderRadius` in pixels stays visually correct at every size
      // — a transform-scaled 16px radius would read as ~2px at thumbnail
      // size, which would break the "smooth into placeholder" feel.
      // The overlay is an empty dark rectangle, so layout-animating a
      // fixed-positioned single element is cheap; no descendants reflow.
      gsap.set(clone, {
        left: srcRect.left,
        top: srcRect.top,
        width: srcRect.width,
        height: srcRect.height,
        opacity: 1,
        borderRadius: 8, // thumbnail's rounded-lg
      });

      episodeTweenRef.current = gsap.to(clone, {
        left: dstRect.left,
        top: dstRect.top,
        width: dstRect.width,
        height: dstRect.height,
        borderRadius: 16, // hero container's effective radius (modal rounded-2xl)
        duration: 0.3,
        ease: "power3.inOut",
        onComplete: () => {
          // Overlay has ARRIVED at the placeholder — only now do we
          // switch the iframe to this episode. This guarantees the video
          // never begins playing before the visual transition has landed.
          setActiveEpisode(episode);
          setPlayingVideo(true);
          // Chain the fade as a NEW tween with a `delay` — one `kill()` on
          // `episodeTweenRef` cancels both morph and fade (or the pending
          // fade during its delay), so overlapping episode clicks don't
          // leave orphaned tweens flying around.
          episodeTweenRef.current = gsap.to(clone, {
            opacity: 0,
            duration: 0.25,
            delay: 0.1,
            ease: "power2.out",
            onComplete: () => {
              episodeTweenRef.current = null;
              setTransitionEpisode(null);
            },
          });
        },
      });
    });
  }, [expandedShow]);

  /* ── ESC key handler ── */
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (playingVideo) {
          setPlayingVideo(false);
        } else if (expandedShow) {
          closeExpanded();
        }
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [expandedShow, playingVideo, closeExpanded]);

  /* ── Main physics loop + entrance ── */
  useEffect(() => {
    registerGSAPPlugins();

    const section = sectionRef.current;
    const slider = sliderRef.current;
    if (!section || !slider) return;

    // Pin the starting angle at INITIAL_CENTER_ID (Solo to SaaS) — the
    // ref initializer sets this on first mount, but React strict-mode
    // double invocations or HMR can leave the angle mid-tween, so we
    // force-reset on every effect run.
    angleState.current.angle = INITIAL_CENTER_ID;
    angleState.current.velocity = 0;
    if (autoTweenRef.current) { autoTweenRef.current.kill(); autoTweenRef.current = null; }
    lastInteraction.current = performance.now();

    // Compute responsive layout
    function updateLayout() {
      const l = computeLayout(window.innerHeight, window.innerWidth);
      layoutRef.current = l;
      setLayout(l);
      console.log("[StreamShowcase] layout updated:", l);

      // If the expanded modal is fully open, resize it to match the new
      // viewport. We only touch it after the open timeline has completed
      // (progress === 1) so we don't fight the flip/scale tweens mid-flight.
      const tl = openTlRef.current;
      const expanded = expandedRef.current;
      if (tl && tl.progress() >= 1 && !tl.reversed() && expanded) {
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const finalW = Math.min(vw - 32, 1200);
        const finalH = vh * 0.92;
        const finalX = (vw - finalW) / 2;
        const finalY = (vh - finalH) / 2;
        gsap.set(expanded, { left: finalX, top: finalY, width: finalW, height: finalH });
      }
    }
    updateLayout();
    window.addEventListener("resize", updateLayout);

    const L = () => layoutRef.current;

    console.log("[StreamShowcase] init:", {
      N,
      ANGLE_PER_CARD: ANGLE_PER_CARD.toFixed(1),
      layout: L(),
      sliderRect: {
        w: slider.clientWidth,
        h: slider.clientHeight,
        top: slider.getBoundingClientRect().top,
      },
      sectionRect: {
        w: section.clientWidth,
        h: section.clientHeight,
      },
      windowSize: { w: window.innerWidth, h: window.innerHeight },
    });

    /* ── Pointer drag → convert to angle delta ── */
    function pointerDown(e: PointerEvent) {
      console.log("[StreamShowcase] pointerDown at x:", e.clientX, "paused:", pausedRef.current);
      if (pausedRef.current) return;
      // Kill auto-advance tween on manual interaction
      if (autoTweenRef.current) {
        autoTweenRef.current.kill();
        autoTweenRef.current = null;
      }
      dragState.current.active = true;
      dragState.current.startX = e.clientX;
      dragState.current.lastX = e.clientX;
      dragState.current.lastTime = performance.now();
      dragState.current.velocity = 0;
      // Don't touch suppressClick here — we decide on pointerUp based on distance
      lastInteraction.current = performance.now();
      // Deliberate interaction → re-enable auto-advance (it may have been
      // suppressed by a prior modal close).
      suppressAutoAdvanceRef.current = false;
    }

    function pointerMove(e: PointerEvent) {
      if (!dragState.current.active) return;
      const dx = e.clientX - dragState.current.lastX;
      const now = performance.now();
      const dt = now - dragState.current.lastTime;

      const angleDelta = -dx * DRAG_SENSITIVITY;
      angleState.current.angle += angleDelta;

      // Smooth velocity: blend with previous (reduces jitter from single-frame spikes)
      if (dt > 0) {
        const instantVelocity = angleDelta / dt * 16;
        dragState.current.velocity = dragState.current.velocity * 0.6 + instantVelocity * 0.4;
      }

      dragState.current.lastX = e.clientX;
      dragState.current.lastTime = now;
      lastInteraction.current = now;
    }

    function pointerUp() {
      if (!dragState.current.active) return;
      dragState.current.active = false;

      // Decide: was this a click or a drag?
      const totalMove = Math.abs(dragState.current.lastX - dragState.current.startX);
      const wasDrag = totalMove > CLICK_THRESHOLD;
      suppressClickRef.current = wasDrag;

      if (wasDrag) {
        // Project momentum from the drag velocity, then GSAP-tween to the nearest card.
        // No spring, no oscillation — just a smooth eased settle.
        const clamped = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, dragState.current.velocity));
        const projected = angleState.current.angle + clamped * FLING_PROJECTION_FRAMES;
        const target = Math.round(projected);

        if (autoTweenRef.current) { autoTweenRef.current.kill(); autoTweenRef.current = null; }
        autoTweenRef.current = gsap.to(angleState.current, {
          angle: target,
          duration: 0.7,
          ease: "power3.out",
          onComplete: () => {
            lastInteraction.current = performance.now();
          },
        });
        console.log("[StreamShowcase] pointerUp fling — v:", clamped.toFixed(3), "target:", target);
      } else {
        // Click — no motion, just kill any residual tween.
        if (autoTweenRef.current) { autoTweenRef.current.kill(); autoTweenRef.current = null; }
      }

      angleState.current.velocity = 0;
      lastInteraction.current = performance.now();

      // Clear suppress after a tick so the click event can read it, then reset
      setTimeout(() => { suppressClickRef.current = false; }, 100);
    }

    slider.addEventListener("pointerdown", pointerDown);
    window.addEventListener("pointermove", pointerMove);
    window.addEventListener("pointerup", pointerUp);

    /* ── Hover pause ── */
    function mouseEnter() { hoverRef.current = true; }
    function mouseLeave() {
      hoverRef.current = false;
      lastInteraction.current = performance.now();
    }
    slider.addEventListener("mouseenter", mouseEnter);
    slider.addEventListener("mouseleave", mouseLeave);

    /* ── Entrance animation ──
     *
     * A single GSAP timeline orchestrates three layered reveals so the
     * section lands with impact instead of fading in piece by piece:
     *
     *   1. Title letters — each character rises from below with a
     *      random rotation + blur that untwists into the final brush-
     *      script layout. Back.out ease gives the letters a playful
     *      overshoot like they're settling into the page.
     *   2. Cards — the physics tick is frozen, cards are seeded at
     *      y: +260, scale: 0.5, rotation: 0 (flat stack below the fold)
     *      then dealt out from center with |offset|-based delay, rising
     *      and rotating into their final fan positions. Physics resumes
     *      on complete.
     *   3. Eyebrow pill — fades + unblurs shortly after the title starts
     *      landing.
     */
    const heroEyebrow = section.querySelector<HTMLElement>("[data-hero-eyebrow]");
    const titleLetters = Array.from(
      section.querySelectorAll<HTMLElement>("[data-title-letter]")
    );
    const cardEls = cardRefs.current.filter(Boolean) as HTMLElement[];

    // Freeze physics so GSAP can own the card transforms during entrance.
    pausedRef.current = true;

    // ── Initial states (what we're animating FROM) ──
    if (heroEyebrow) {
      gsap.set(heroEyebrow, {
        opacity: 0,
        y: 24,
        filter: "blur(8px)",
      });
    }
    gsap.set(titleLetters, {
      y: 80,
      opacity: 0,
      scale: 0.4,
      rotation: () => gsap.utils.random(-22, 22),
      filter: "blur(14px)",
      transformOrigin: "50% 100%",
    });
    gsap.set(cardEls, {
      y: 260,
      opacity: 0,
      scale: 0.55,
      rotation: 0,
    });

    // Pre-populate the style fields the physics tick normally writes per-
    // frame (inner scale, card filter/z-index, image opacity, meta/overlay/
    // gradient opacity). Without this, cards would visibly "pop" the moment
    // the tick resumes because the side cards would instantly gain their
    // grayscale + inner-scale + meta-hidden treatment.
    // Offsets are computed relative to the INITIAL_CENTER_ID so card 2
    // (Solo to SaaS) gets the "center" treatment (full color, meta shown)
    // and cards on either side get the progressive grayscale + shrink
    // that the physics tick would have written at angle=2.
    const halfNInit = N / 2;
    const wrapOffset = (rawOffset: number) => {
      return ((rawOffset % N) + N + halfNInit) % N - halfNInit;
    };
    cardEls.forEach((card, i) => {
      const offset = wrapOffset(i - INITIAL_CENTER_ID);
      const absOff = Math.abs(offset);
      const centerness = Math.max(0, Math.min(1, 1 - absOff));
      const isCenter = absOff < 0.4;

      card.style.filter = `grayscale(${(1 - centerness) * 100}%)`;
      // Cap at z-30 (center) descending to z-20 (far): the PreviewStage's
      // "Copy Prompt" popup sits at z-50 and must stay above every card.
      // Center-card primacy over side cards only needs ~10 units of
      // headroom, not 100 like the old formula.
      card.style.zIndex = String(Math.round(30 - absOff * 2));

      const inner = cardInnerRefs.current[i];
      if (inner) {
        const innerScale = 1 - Math.min(absOff, 1) * 0.15;
        gsap.set(inner, { scale: innerScale });
      }

      const img = card.querySelector<HTMLImageElement>("img");
      if (img) img.style.opacity = String(0.5 + centerness * 0.5);

      const meta = card.querySelector<HTMLElement>("[data-card-meta]");
      const overlay = card.querySelector<HTMLElement>("[data-card-overlay]");
      const gradient = card.querySelector<HTMLElement>("[data-card-gradient]");
      if (meta) {
        meta.style.opacity = isCenter ? "1" : "0";
        meta.style.transform = isCenter ? "translateY(0)" : "translateY(8px)";
      }
      if (overlay) overlay.style.opacity = isCenter ? "1" : "0";
      if (gradient) gradient.style.opacity = isCenter ? "1" : "0";
    });

    // ── Timeline ──
    const entranceTl = gsap.timeline({
      delay: 0.1,
      onComplete: () => {
        // Physics resumes — tick will write inline styles again.
        pausedRef.current = false;
        lastInteraction.current = performance.now();
      },
    });

    // 1. Title letters — stagger from left, playful overshoot.
    entranceTl.to(titleLetters, {
      y: 0,
      opacity: 1,
      scale: 1,
      rotation: 0,
      filter: "blur(0px)",
      duration: 0.75,
      ease: "back.out(1.7)",
      stagger: 0.035,
    }, 0);

    // 2. Cards — rise from below, spread into fan.
    //    Delay each card by |offset| × 0.08s so the center card lands
    //    first and the pair to each side follows, giving a "deal outward"
    //    feel rather than a uniform curtain rise. Offsets are wrapped
    //    around INITIAL_CENTER_ID so Solo to SaaS is the first to land.
    cardEls.forEach((card, i) => {
      const offset = wrapOffset(i - INITIAL_CENTER_ID);
      const absOff = Math.abs(offset);
      const finalRotation = offset * ROTATION_PER_CARD;
      entranceTl.to(
        card,
        {
          y: 0,
          opacity: 1,
          scale: 1,
          rotation: finalRotation,
          duration: 0.9,
          ease: "back.out(1.25)",
        },
        0.45 + absOff * 0.09,
      );
    });

    // 3. Eyebrow pill — fades in slightly after the title starts landing.
    if (heroEyebrow) {
      entranceTl.to(
        heroEyebrow,
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.75,
          ease: "power3.out",
        },
        0.7,
      );
    }

    /* ── Physics loop ── */
    let lastLogTime = 0;
    function tick() {
      const now = performance.now();
      const ds = dragState.current;

      // When paused (expanded view animating), yield full control to GSAP —
      // don't overwrite card.style.transform / opacity / filter, or they'd fight
      // the open/close timeline and the other cards would re-appear mid-animation.
      if (pausedRef.current) {
        frameRef.current = requestAnimationFrame(tick);
        return;
      }

      // No spring physics here — all snap/advance motion is driven by GSAP tweens
      // owned by autoTweenRef. That removes the oscillation/shake that spring-damper
      // snaps produce. This block only triggers auto-advance when the user is idle.
      if (!ds.active) {
        const idle = now - lastInteraction.current > AUTO_DELAY;
        const hasActiveTween = !!autoTweenRef.current && autoTweenRef.current.isActive();
        if (idle && !hoverRef.current && !hasActiveTween && !suppressAutoAdvanceRef.current) {
          const nearestSnap = Math.round(angleState.current.angle);
          const target = nearestSnap + 1;
          console.log("[StreamShowcase] auto-advance → target:", target.toFixed(1));
          autoTweenRef.current = gsap.to(angleState.current, {
            angle: target,
            duration: 1.0,
            ease: "power2.inOut",
            onComplete: () => {
              lastInteraction.current = performance.now();
            },
          });
        }
      }

      // Position each card along a 2D arc.
      // angle (card-units) = 0 means card 0 centered at the top of the arc.
      const currentPos = angleState.current.angle;

      // Push the rounded center index into React state, but only when it
      // changes — the eyebrow chip reads this to show the live playlist
      // name. Guarding on the ref keeps the component from re-rendering
      // every frame.
      const roundedCenter = ((Math.round(currentPos) % N) + N) % N;
      if (roundedCenter !== lastCenterIdxRef.current) {
        lastCenterIdxRef.current = roundedCenter;
        setCenterIdx(roundedCenter);
      }

      // Throttled debug log every 2s
      const shouldLog = now - lastLogTime > 2000;
      if (shouldLog) {
        lastLogTime = now;
        const idle = now - lastInteraction.current > AUTO_DELAY;
        console.log("[StreamShowcase] tick:", {
          pos: currentPos.toFixed(3),
          velocity: angleState.current.velocity.toFixed(4),
          centerCard: roundedCenter,
          idle,
          hovered: hoverRef.current,
          paused: pausedRef.current,
          dragging: ds.active,
        });
      }

      const halfN = N / 2;

      for (let i = 0; i < N; i++) {
        const card = cardRefs.current[i];
        if (!card) continue;

        // Offset from center, wrapped into [-N/2, +N/2] so cards can scroll infinitely.
        let offset = i - currentPos;
        offset = ((offset % N) + N + halfN) % N - halfN;

        const absOff = Math.abs(offset);

        // Smooth fade near the wrap boundary (±halfN) so cards don't pop.
        const fadeStart = halfN - 0.5;
        const wrapFade = absOff > fadeStart
          ? Math.max(0, 1 - (absOff - fadeStart) / 0.5)
          : 1;
        if (wrapFade <= 0) {
          card.style.opacity = "0";
          card.style.pointerEvents = "none";
          continue;
        }
        card.style.pointerEvents = "auto";

        // Fan rotation on OUTER — around the shared pivot below all cards.
        const rotation = offset * ROTATION_PER_CARD;
        card.style.transform = `rotate(${rotation}deg)`;

        // Scale on INNER — around the card's own center, so side cards shrink
        // in place instead of being pulled toward the pivot.
        const inner = cardInnerRefs.current[i];
        if (inner) {
          // Smooth interpolation: 1.0 at offset 0, 0.85 once |offset| >= 1.
          const scale = 1 - Math.min(absOff, 1) * 0.15;
          inner.style.transform = `scale(${scale})`;
        }

        // Center card sits on top of the stack — kept below z-50 so the
        // gallery "Copy Prompt" popup (z-50 in PreviewStage) always
        // renders above the slider. See matching cap in the entrance
        // pre-populate above.
        card.style.zIndex = `${Math.round(30 - absOff * 2)}`;

        const isCenter = absOff < 0.4;

        // Card itself is ALWAYS fully opaque (so overlapping cards don't bleed
        // through each other). Only the wrap boundary can fade the card out.
        card.style.opacity = `${wrapFade}`;

        // Continuous color ↔ grayscale based on how close the card is to
        // center — 1.0 at offset 0, 0 once |offset| >= 1. Avoids the binary
        // pop that a threshold-based toggle produces while dragging.
        const centerness = Math.max(0, Math.min(1, 1 - absOff));
        card.style.filter = `grayscale(${(1 - centerness) * 100}%)`;

        // The card image fades with the same curve so colors desaturate AND
        // dim together. The card background (set in JSX) stays solid.
        const img = card.querySelector<HTMLImageElement>("img");
        if (img) {
          img.style.opacity = `${0.5 + centerness * 0.5}`;
        }

        // Metadata + overlay + bottom gradient only visible on the centered
        // card — these are the "text layer" and fade together.
        const meta = card.querySelector<HTMLElement>("[data-card-meta]");
        const overlay = card.querySelector<HTMLElement>("[data-card-overlay]");
        const gradient = card.querySelector<HTMLElement>("[data-card-gradient]");
        if (meta) {
          meta.style.opacity = isCenter ? "1" : "0";
          meta.style.transform = isCenter ? "translateY(0)" : "translateY(8px)";
        }
        if (overlay) {
          overlay.style.opacity = isCenter ? "1" : "0";
        }
        if (gradient) {
          gradient.style.opacity = isCenter ? "1" : "0";
        }
      }

      frameRef.current = requestAnimationFrame(tick);
    }

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameRef.current);
      slider.removeEventListener("pointerdown", pointerDown);
      window.removeEventListener("pointermove", pointerMove);
      window.removeEventListener("pointerup", pointerUp);
      slider.removeEventListener("mouseenter", mouseEnter);
      slider.removeEventListener("mouseleave", mouseLeave);
      window.removeEventListener("resize", updateLayout);
      if (autoTweenRef.current) autoTweenRef.current.kill();
      entranceTl.kill();
    };
  }, []);

  if (!SHOWS.length) {
    return (
      <section
        className={`relative flex h-screen w-full items-center justify-center bg-[#f5f5f7] dark:bg-[#0a0a0c] ${className ?? ""}`}
      >
        <p className="text-sm text-[#666] dark:text-white/50">
          No playlists yet.
        </p>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={`relative h-screen w-full overflow-hidden bg-[#f5f5f7] dark:bg-[#0a0a0c] ${className ?? ""}`}
    >
      {/* ── Header content ──
          The hero is framed like a streaming platform's shelf header:
          live eyebrow (updates as the carousel auto-advances), editorial
          title, one-line pitch naming the audience, a stats row that
          makes the shelf feel alive, and an iconified category rail. */}
      <div className="relative z-10 flex flex-col items-center pt-16 md:pt-24 px-4 md:px-6">
        {/* Eyebrow — tracks the currently centered playlist */}
        <div
          data-hero-eyebrow
          className="flex items-center gap-2 rounded-full border border-black/8 bg-white/70 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-[#666] shadow-[0_1px_2px_rgba(0,0,0,0.03)] backdrop-blur-md dark:border-white/10 dark:bg-white/[0.04] dark:text-[#9a9a9a]"
        >
          <span className="relative flex h-1.5 w-1.5 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#1a1a1a]/40 dark:bg-white/40" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#1a1a1a] dark:bg-white" />
          </span>
          <span>Now browsing</span>
          <span className="text-[#1a1a1a]/25 dark:text-white/25">/</span>
          <span className="font-semibold tracking-[0.08em] normal-case text-[#1a1a1a] dark:text-white">
            {SHOWS[centerIdx]?.title ?? "—"}
          </span>
        </div>

        {/* Big playful brush-script title — Airbnb-style hand lettering.
            Each letter is its own span so GSAP can stagger per-letter
            entrance (rise + rotate + blur + scale with back.out ease). */}
        <h1
          data-hero-title
          className="mt-4 text-center font-normal text-[#1a1a1a] dark:text-white"
          style={{
            fontFamily: "var(--font-caveat-brush), 'Caveat Brush', cursive",
            // Floor at 3.25rem so "Queue up." fits on a 320px iPhone SE
            // with px-4 padding; max at 9rem so it doesn't dominate
            // ultra-wide displays.
            fontSize: "clamp(3.25rem, 11vw, 9rem)",
            lineHeight: 0.9,
            letterSpacing: "-0.01em",
          }}
        >
          {["Queue up.", "Level up."].map((line, lineIdx) => (
            <span key={lineIdx} className="block">
              {Array.from(line).map((char, i) => (
                <span
                  key={`${lineIdx}-${i}`}
                  data-title-letter
                  className="inline-block"
                  style={{ willChange: "transform, opacity, filter" }}
                >
                  {char === " " ? "\u00A0" : char}
                </span>
              ))}
            </span>
          ))}
        </h1>
      </div>

      {/* ── 2D Arc Slider ── */}
      {/*
        Flat 2D slider: cards are arranged along a circular arc so the center
        card sits at the top and adjacent cards drop down progressively.
      */}
      <div
        ref={sliderRef}
        className="absolute inset-x-0 select-none"
        style={{
          top: 0,
          bottom: 0,
          cursor: "grab",
          // `pan-y` tells the browser it handles vertical scroll gestures
          // itself but leaves horizontal gestures for our pointer handler
          // — on mobile, without this the browser would often decide a
          // slightly-diagonal swipe was a page scroll and swallow our
          // drag, making the carousel feel unresponsive.
          touchAction: "pan-y",
        }}
      >
        {/* Arc origin — where the center (offset 0) card sits */}
        <div
          className="absolute"
          style={{
            left: "50%",
            top: `${layout.centerYRatio * 100}%`,
          }}
        >
          {SHOWS.map((show, i) => (
            <div
              key={show.id}
              ref={(el) => { cardRefs.current[i] = el; }}
              onClickCapture={(e) => {
                // Check suppress synchronously — pointerUp sets it based on drag distance
                if (suppressClickRef.current) {
                  console.log("[StreamShowcase] click suppressed (was dragging)");
                  e.stopPropagation();
                  return;
                }

                // Compute this card's wrapped offset from the current center.
                const currentPos = angleState.current.angle;
                const half = N / 2;
                let offset = i - currentPos;
                offset = ((offset % N) + N + half) % N - half;
                console.log("[StreamShowcase] card clicked:", show.title, "offset:", offset.toFixed(2));

                // Snapshot the slider at the instant of click, then again 100ms
                // and 1000ms later. If a glitch shows up on click, it'll be
                // visible as a state diff between these three snapshots.
                snapshotSlider("click:0ms");
                setTimeout(() => snapshotSlider("click:100ms"), 100);
                setTimeout(() => snapshotSlider("click:1000ms"), 1000);

                if (Math.abs(offset) < 0.5) {
                  // Already centered — open directly.
                  openExpanded(show);
                } else {
                  // Slide the clicked card to center first (shortest-direction
                  // tween), THEN open the modal. The slide permanently updates
                  // the slider angle — after close, auto-advance is suppressed,
                  // so the clicked card remains centered as the new "current"
                  // card. Future clicks are relative to this new ordering.
                  const target = currentPos + offset;
                  if (autoTweenRef.current) { autoTweenRef.current.kill(); autoTweenRef.current = null; }
                  console.log("[StreamShowcase] sliding to center → target:", target.toFixed(2), "then open");
                  autoTweenRef.current = gsap.to(angleState.current, {
                    angle: target,
                    duration: 0.45,
                    ease: "power3.out",
                    onComplete: () => {
                      autoTweenRef.current = null;
                      openExpanded(show);
                    },
                  });
                }
              }}
              className="absolute cursor-pointer"
              style={{
                width: layout.cardW,
                height: layout.cardH,
                marginLeft: -layout.cardW / 2,
                marginTop: -layout.cardH / 2,
                willChange: "transform, opacity, filter",
                // Shared pivot below all cards — creates the fan effect when rotated
                transformOrigin: `50% ${PIVOT_Y_PERCENT}%`,
              }}
            >
              {/* Inner wrapper — scales around its own center so non-center
                  cards shrink in place instead of drifting toward the pivot. */}
              <div
                ref={(el) => { cardInnerRefs.current[i] = el; }}
                className="absolute inset-0 bg-white dark:bg-[#1a1a1e]"
                style={{
                  borderRadius: 16,
                  overflow: "hidden",
                  transformOrigin: "50% 50%",
                  willChange: "transform",
                }}
              >
                {/* Card image */}
                <div className="absolute inset-0">
                  <img
                    src={show.image}
                    alt={show.title}
                    className="h-full w-full object-cover"
                    draggable={false}
                    onError={downgradeToHqOnError}
                  />
                  <div
                    data-card-gradient
                    className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/40 to-transparent"
                    style={{ transition: "opacity 0.3s ease" }}
                  />
                </div>

                {/* Top overlay: rating + type badge */}
                <div
                  data-card-overlay
                  className="absolute inset-x-0 top-0 flex items-start justify-between p-3"
                  style={{ transition: "opacity 0.3s ease" }}
                >
                  <div className="flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 backdrop-blur-md">
                    <span className="text-yellow-400"><StarIcon /></span>
                    <span className="text-[11px] font-semibold text-white">{show.rating}</span>
                  </div>
                  <div className="flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 backdrop-blur-md">
                    <span className="text-white/80"><TypeIcon type={show.type} /></span>
                    <span className="text-[10px] font-medium capitalize text-white/80">
                      {show.type === "series" ? "TV Show" : show.type}
                    </span>
                  </div>
                </div>

                {/* Bottom metadata */}
                <div
                  data-card-meta
                  className="absolute inset-x-0 bottom-0 p-4"
                  style={{ transition: "opacity 0.35s ease, transform 0.35s ease" }}
                >
                  <h3 className="text-lg font-bold text-white tracking-tight">{show.title}</h3>
                  <div className="mt-1 flex items-center gap-2 text-[11px] text-white/70">
                    <span>{show.year}</span>
                    <span className="h-1 w-1 rounded-full bg-white/40" />
                    <span>{show.seasons}</span>
                    <span className="h-1 w-1 rounded-full bg-white/40" />
                    <span>{show.episodes}</span>
                  </div>
                </div>

                <div
                  className="pointer-events-none absolute inset-0 rounded-[16px]"
                  style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)" }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Backdrop ── */}
      <div
        ref={backdropRef}
        className="fixed inset-0 z-[998] bg-white/90 dark:bg-black/70 backdrop-blur-sm"
        style={{ opacity: 0, pointerEvents: expandedShow ? "auto" : "none" }}
        onClick={closeExpanded}
      />

      {/* ── Expanded overlay ── */}
      {/* The modal appears at the card's rect and visually REPLACES it, so
          every visual attribute (bg, radius, COMPOSITING) must match the
          card inner exactly. Any mismatch reads as a jump/pop at the moment
          of handover (open AND close).
          Matched to card inner: same bg, same 16px radius, NO boxShadow
          (removed from both to keep the slider minimal). Matched to card
          OUTER: willChange + filter: grayscale(0%). The card outer carries
          both of those (willChange in JSX, filter written every frame by
          the physics tick), which forces the card into its own GPU
          compositing layer. A layer-promoted element can rasterize with a
          subpixel offset vs. a non-promoted one, so even with identical CSS
          the swap can look like a tiny jump. grayscale(0%) is a no-op filter
          — it's purely there to promote the modal to a layer so rasterization
          matches the card's. */}
      <div
        ref={expandedRef}
        className="fixed z-[999] overflow-hidden rounded-2xl bg-white dark:bg-transparent"
        style={{
          visibility: expandedShow ? "visible" : "hidden",
          pointerEvents: expandedShow ? "auto" : "none",
          willChange: "transform, opacity, filter",
          filter: "grayscale(0%)",
        }}
      >
        {expandedShow && (
          <>
            {/* FRONT — card poster (mirrors the grid card's layout so the
                badge/title/metadata stay visible throughout the flip instead
                of disappearing the moment the modal opens). This whole layer
                fades to 0 at flipMid, so the overlays naturally fade with it. */}
            <div
              data-poster
              className="absolute inset-0 overflow-hidden rounded-2xl"
            >
              <img
                src={expandedShow.image}
                alt={expandedShow.title}
                className="h-full w-full object-cover"
                onError={downgradeToHqOnError}
              />
              {/* Gradient scrim at the bottom (for meta legibility) */}
              <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

              {/* Top overlay: rating + type badge */}
              <div className="absolute inset-x-0 top-0 flex items-start justify-between p-3">
                <div className="flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 backdrop-blur-md">
                  <span className="text-yellow-400"><StarIcon /></span>
                  <span className="text-[11px] font-semibold text-white">{expandedShow.rating}</span>
                </div>
                <div className="flex items-center gap-1 rounded-full bg-black/50 px-2.5 py-1 backdrop-blur-md">
                  <span className="text-white/80"><TypeIcon type={expandedShow.type} /></span>
                  <span className="text-[10px] font-medium capitalize text-white/80">
                    {expandedShow.type === "series" ? "TV Show" : expandedShow.type}
                  </span>
                </div>
              </div>

              {/* Bottom metadata: title, year, seasons, episodes */}
              <div className="absolute inset-x-0 bottom-0 p-4">
                <h3 className="text-lg font-bold text-white tracking-tight">{expandedShow.title}</h3>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-white/70">
                  <span>{expandedShow.year}</span>
                  <span className="h-1 w-1 rounded-full bg-white/40" />
                  <span>{expandedShow.seasons}</span>
                  <span className="h-1 w-1 rounded-full bg-white/40" />
                  <span>{expandedShow.episodes}</span>
                </div>
              </div>

              {/* 1px inset highlight, matches the grid card */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{ boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12)" }}
              />
            </div>

            {/* BACK — detail view (visible after flip) */}
            {/* No background color: the backdrop already dims the scene, so
                keeping the detail transparent looks cleaner — the episodes
                section blends into the dimmed scene behind it. */}
            <div
              data-detail
              className="absolute inset-0 flex flex-col overflow-hidden rounded-2xl"
            >
              {/* Close button */}
              <button
                onClick={closeExpanded}
                className="absolute right-4 top-4 z-50 flex h-9 w-9 items-center justify-center rounded-full bg-black/30 text-white/90 backdrop-blur-md transition-colors hover:bg-black/50"
              >
                <CloseIcon />
              </button>

              {/* Hero image / Video — modal is 92vh with 4vh top margin, reserve 13rem for episodes */}
              {/* bg-[#111] is the "video placeholder" color: when the user
                  clicks an episode, React swaps the <img> for a YouTube
                  <iframe>, which takes a beat to initialize + load. Without
                  a background, the modal's white bg flashes through during
                  that window. A near-black grey mimics YouTube's own
                  loading color so the handoff to the actual video is
                  seamless. */}
              <div
                ref={heroContainerRef}
                className="relative w-full overflow-hidden rounded-b-2xl bg-[#111]"
                style={{ height: "calc(92vh - 13rem)" }}
              >
                {playingVideo && activeEpisode ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${activeEpisode.videoId}?autoplay=1&rel=0&modestbranding=1`}
                    className="h-full w-full"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    style={{ border: 0 }}
                  />
                ) : (
                  <>
                    {/* Preview image = the playlist's hand-picked "Full"
                        artwork from /public/stream. Stays consistent per
                        show so the modal has a clear identity before the
                        user hits play. */}
                    <img
                      src={expandedShow.backImage}
                      alt={expandedShow.title}
                      className="h-full w-full object-cover"
                      onError={downgradeToHqOnError}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

                    {/* Detail overlay on image — animated in Phase 3 */}
                    <div data-detail-overlay className="absolute bottom-5 left-6 z-10">
                      <div className="flex gap-2 mb-2.5">
                        {expandedShow.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-full bg-white/15 px-3 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-md"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h2
                        className="text-2xl font-bold text-white tracking-tight sm:text-3xl"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {expandedShow.title}
                      </h2>
                      <div className="mt-1.5 flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-yellow-400"><StarIcon /></span>
                          <span className="text-sm font-semibold text-white">{expandedShow.rating}</span>
                          <span className="text-xs text-white/50">/ 10</span>
                        </div>
                        <span className="text-xs text-white/50">&bull;</span>
                        <span className="text-xs text-white/60">{expandedShow.year}</span>
                        <span className="text-xs text-white/50">&bull;</span>
                        <span className="text-xs text-white/60">{expandedShow.seasons}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Episode list — light themed bottom section */}
              <div className="flex flex-1 flex-col px-6 pt-4 pb-3 overflow-hidden">
                <div data-episode-header className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold text-[#1a1a1a] dark:text-white">Episodes</h3>
                    <span className="text-[11px] text-[#999] dark:text-white/40">{expandedShow.seasons}</span>
                  </div>
                  {playingVideo && (
                    <button
                      onClick={() => setPlayingVideo(false)}
                      className="rounded-full bg-[#f0f0f0] dark:bg-white/10 px-3 py-1 text-[10px] font-medium text-[#555] dark:text-white/70 transition-colors hover:bg-[#e0e0e0] dark:hover:bg-white/15"
                    >
                      Back to preview
                    </button>
                  )}
                </div>

                <div
                  className="flex gap-3 overflow-x-auto pb-2"
                  data-episode-list
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" } as React.CSSProperties}
                >
                  {expandedShow.episodeList.map((ep) => (
                    <button
                      key={ep.id}
                      onClick={(e) => playEpisode(ep, e.currentTarget)}
                      className="group/ep flex-shrink-0 rounded-xl overflow-hidden"
                      style={{ width: 160 }}
                    >
                      <div data-ep-thumb className="relative aspect-video overflow-hidden rounded-lg">
                        <img
                          src={ytThumb(ep.videoId)}
                          alt={ep.title}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover/ep:scale-105"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 transition-opacity group-hover/ep:opacity-100">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/25 backdrop-blur-sm">
                            <PlayIcon size={12} />
                          </div>
                        </div>
                        {activeEpisode?.id === ep.id && playingVideo && (
                          <div className="absolute bottom-1 right-1 flex items-center gap-0.5 rounded-full bg-black px-2 py-0.5">
                            <span className="text-[8px] font-bold text-white">PLAYING</span>
                          </div>
                        )}
                      </div>
                      <div className="p-1.5 text-left">
                        <p className={`text-[11px] leading-tight transition-colors duration-200 ${
                          activeEpisode?.id === ep.id && playingVideo
                            ? "font-semibold text-[#1a1a1a] dark:text-white"
                            : "font-medium text-[#555] dark:text-white/75"
                        }`}>
                          {ep.title}
                        </p>
                        <p className="mt-0.5 text-[10px] text-[#999] dark:text-white/40">{ep.duration}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Episode-click transition overlay ──
          A translucent wash (15%) that flies from the clicked thumbnail
          up to the hero area. The tint inverts against the theme so the
          overlay is always just-visible: on a LIGHT page, 15% black adds
          a subtle darkening wash; on a DARK page, 15% white adds a
          subtle lightening wash. Lives at section level (outside the
          modal's overflow:hidden) so the morph spans the full viewport.
          `opacity: 0` at mount means even if React paints before the rAF
          fires, the overlay never flashes in its initial top-left
          position. */}
      {transitionEpisode && (
        <div
          ref={episodeCloneRef}
          className="fixed z-[1001] pointer-events-none bg-black/15 dark:bg-white/15"
          style={{
            willChange: "left, top, width, height, border-radius, opacity",
            opacity: 0,
            left: 0,
            top: 0,
          }}
        />
      )}

    </section>
  );
}
